import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  X,
  BookOpen,
  ClipboardList,
  ClipboardCheck,
  FileText,
  Compass,
  Loader2,
  CornerDownLeft,
} from 'lucide-react';
import useStudentSearch from '../../hooks/useStudentSearch';
import './CommandPalette.scss';

// Section order + presentation. `actions` is the local navigation group; the
// rest map to the backend `groups` keys.
const SECTIONS = [
  { key: 'actions', label: 'Pages', Icon: Compass },
  { key: 'courses', label: 'Courses', Icon: BookOpen },
  { key: 'assignments', label: 'Assignments', Icon: ClipboardList },
  { key: 'quizzes', label: 'Quizzes & Tests', Icon: ClipboardCheck },
  { key: 'materials', label: 'Materials', Icon: FileText },
];

const TYPE_ICON = {
  action: Compass,
  course: BookOpen,
  assignment: ClipboardList,
  quiz: ClipboardCheck,
  material: FileText,
};

// Highlight the matched substring in a result title.
function Highlight({ text = '', query = '' }) {
  const q = query.trim();
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="cmdk__mark">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

// `shortcut` is the key hint's words: the student desk names Ctrl K on Windows, where ⌘ means nothing.
const CommandPalette = ({ placeholder = "Search courses, assignments...", shortcut = "⌘K" }) => {
  const navigate = useNavigate();
  const { query, setQuery, groups, actions, loading, error, reset } = useStudentSearch();

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const containerRef = useRef(null);

  const focusSearch = useCallback(() => inputRef.current?.focus(), []);

  // ⌘K / Ctrl-K focuses the palette input.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        focusSearch();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusSearch]);

  // Build the ordered list of visible sections (with items).
  const sections = useMemo(() => {
    return SECTIONS.map((s) => ({
      ...s,
      items: s.key === 'actions' ? actions : groups[s.key] || [],
    })).filter((s) => s.items.length > 0);
  }, [actions, groups]);

  // Flatten for keyboard navigation: a single index across all options.
  const flatItems = useMemo(
    () => sections.flatMap((s) => s.items.map((item) => ({ ...item, sectionKey: s.key }))),
    [sections]
  );

  const hasQuery = query.trim().length >= 2;
  const showPanel = open && hasQuery;
  const showEmpty = showPanel && !loading && !error && flatItems.length === 0;

  // Reset highlight to the first item whenever the result set changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [flatItems.length, query]);

  // Keep the active option scrolled into view.
  useEffect(() => {
    if (!showPanel) return;
    const el = panelRef.current?.querySelector(`#cmdk-opt-${activeIndex}`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, showPanel]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const go = useCallback(
    (item) => {
      if (!item?.url) return;
      navigate(item.url);
      reset();
      setOpen(false);
      inputRef.current?.blur();
    },
    [navigate, reset]
  );

  // Fallback: "see all results" → the browse/catalog page with the query.
  const seeAll = useCallback(() => {
    const q = query.trim();
    if (!q) return;
    navigate(`/s/courses?q=${encodeURIComponent(q)}`);
    reset();
    setOpen(false);
    inputRef.current?.blur();
  }, [navigate, query, reset]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (query) {
        reset();
      } else {
        setOpen(false);
        inputRef.current?.blur();
      }
      return;
    }
    if (!showPanel) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (flatItems.length ? (i + 1) % flatItems.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (flatItems.length ? (i - 1 + flatItems.length) % flatItems.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatItems[activeIndex]) go(flatItems[activeIndex]);
      else seeAll();
    }
  };

  // Map a section item to its position in the flattened list (for ids/active state).
  let runningIndex = -1;

  return (
    <div className="command-palette top-nav-search d-none d-md-flex" ref={containerRef}>
      <Search size={16} className="top-nav-search__icon" aria-hidden="true" />
      <input
        ref={inputRef}
        id="sd-topnav-search"
        type="text"
        name="q"
        className="top-nav-search__input"
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
        role="combobox"
        aria-label="Search courses, assignments, quizzes and materials"
        aria-expanded={showPanel}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        aria-controls="cmdk-listbox"
        aria-activedescendant={showPanel && flatItems.length ? `cmdk-opt-${activeIndex}` : undefined}
      />
      {loading && hasQuery ? (
        <Loader2 size={15} className="cmdk__spinner" aria-hidden="true" />
      ) : query ? (
        <button
          type="button"
          className="cmdk__clear"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { reset(); focusSearch(); }}
          aria-label="Clear search"
        >
          <X size={14} />
        </button>
      ) : (
        <kbd
          className="top-nav-search__kbd"
          title="Focus search"
          onMouseDown={(e) => e.preventDefault()}
          onClick={focusSearch}
        >
          {shortcut}
        </kbd>
      )}

      {showPanel && (
        <div className="cmdk__panel" ref={panelRef}>
          <ul id="cmdk-listbox" role="listbox" className="cmdk__list" aria-label="Search results">
            {error && (
              <li className="cmdk__state cmdk__state--error" role="presentation">
                Something went wrong. Please try again.
              </li>
            )}

            {showEmpty && (
              <li className="cmdk__state" role="presentation">
                No results for “{query.trim()}”
              </li>
            )}

            {!error &&
              sections.map((section) => (
                <li key={section.key} role="presentation" className="cmdk__group">
                  <div className="cmdk__group-label">{section.label}</div>
                  <ul role="presentation" className="cmdk__group-list">
                    {section.items.map((item) => {
                      runningIndex += 1;
                      const index = runningIndex;
                      const ItemIcon = item.Icon || TYPE_ICON[item.type] || section.Icon;
                      const isActive = index === activeIndex;
                      return (
                        <li
                          key={`${section.key}-${item.id}`}
                          id={`cmdk-opt-${index}`}
                          role="option"
                          aria-selected={isActive}
                          className={`cmdk__option${isActive ? ' is-active' : ''}`}
                          onMouseEnter={() => setActiveIndex(index)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => go(item)}
                        >
                          <ItemIcon size={16} className="cmdk__option-icon" aria-hidden="true" />
                          <span className="cmdk__option-text">
                            <span className="cmdk__option-title">
                              <Highlight text={item.title} query={query} />
                            </span>
                            {item.subtitle && (
                              <span className="cmdk__option-subtitle">{item.subtitle}</span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
          </ul>

          {!error && (
            <button type="button" className="cmdk__footer" onMouseDown={(e) => e.preventDefault()} onClick={seeAll}>
              <span>See all results for “{query.trim()}”</span>
              <kbd className="cmdk__footer-kbd"><CornerDownLeft size={12} /> Enter</kbd>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CommandPalette;
